import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, Output, EventEmitter, inject, ChangeDetectorRef } from '@angular/core';
import { NbCardModule, NbButtonModule, NbListModule, NbSpinnerModule, NbDialogService } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, NgStyle } from '@angular/common';
import { KPI, APIService } from '../../../@core/api';
import { InvestmentsService, ScopeService, I18nService, RoadmapService, SettingsService } from '../../../@core/utils';
import { InvestmentWizardService } from '../../../@core/utils/investment-wizard-service';
import { BaseDrawerComponent } from '../side-forms/base-drawer/base-drawer.component';
import { combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { InvestmentWizardRootComponent } from '../wizards/investment-wizard/wizard-index.component';
import { OutsideClickDirective } from '../../directives/outside-click.directive';
import { LinkableInvestmentsListComponent } from '../side-forms/linkable-investments-list/linkable-investments-list.component';
import { BasicDialogComponent } from '../basic-dialog/basic-dialog.component';

export interface InvestmentListItem {
  id: number;
  label: string;
  status: string;
  budget: number;
  budgetFormatted: string;
  year: number;
  estimateText: string;
  impactIndicators: string;
  investment?: KPI.MappedInvestment;
}

@Component({
  selector: 'app-investment-list-drawer',
  templateUrl: './investment-list-drawer.component.html',
  styleUrls: ['./investment-list-drawer.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    NbListModule,
    NbSpinnerModule,
    TranslateModule,
    CommonModule,
    NgStyle,
    OutsideClickDirective
  ],
})
export class InvestmentListDrawerComponent extends BaseDrawerComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public investmentIds: number[] = [];
  @Input() public investmentData: any[] = [];
  @Input() public globalInvestments: KPI.MinimalInvestment[] = [];
  @Input() public asset: KPI.MappedAsset | null = null;
  @Input() public assetId: number | null = null;
  @Output() public drawerClosed = new EventEmitter<void>();
  @Output() public investmentSelected = new EventEmitter<number>();
  @Output() public investmentsAttached = new EventEmitter<number[]>();

  public investments: InvestmentListItem[] = [];
  public loading: boolean = true;
  public showAttachMenu: boolean = false;
  public activeTab: 'capex' | 'opex' | 'amortissement' = 'capex';
  public opexItems: KPI.AssetOpex[] = [];
  public loadingOpex: boolean = false;

  private investmentsService = inject(InvestmentsService);
  private scopeService = inject(ScopeService);
  private translate = inject(TranslateService);
  private i18n = inject(I18nService);
  private cdr = inject(ChangeDetectorRef);
  private dialogService = inject(NbDialogService);
  private roadmapService = inject(RoadmapService);
  private api = inject(APIService);
  private settingsService = inject(SettingsService);

  public opexManagementEnabled$ = this.settingsService.opexManagement$;
  private ivWizardService = inject(InvestmentWizardService);

  public ngOnInit(): void {
    super.ngOnInit();
    if (this.visible) {
      this.handleVisibilityChange();
    } else {
      this.loading = false;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    const visibleChanged = changes['visible'] && changes['visible'].currentValue === true;
    const dataChangedWhileVisible = this.visible && (changes['investmentIds'] || changes['investmentData']);

    if (visibleChanged) {
      this.investments = [];
      this.handleVisibilityChange();
    } else if (dataChangedWhileVisible) {
      if (changes['investmentIds'] && this.investmentIds?.length > 0) {
        this.loading = true;
        this.cdr.detectChanges();
        this.handleVisibilityChange();
      } else if (!this.investmentIds?.length && this.investmentData?.length > 0) {
        this.loadInvestmentsFromData(false);
      } else if (!this.investmentIds?.length && !this.investmentData?.length) {
        this.investments = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    } else if (changes['visible'] && !this.visible) {
      this.investments = [];
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private handleVisibilityChange(): void {
    if (this.investmentIds && this.investmentIds.length > 0) {
      this.loadInvestments();
    } else if (this.investmentData && this.investmentData.length > 0) {
      this.loadInvestmentsFromData();
    } else {
      this.loading = false;
      this.investments = [];
      this.cdr.detectChanges();
    }
    this.loadOpex();
  }

  private loadInvestmentsFromData(showLoading: boolean = true): void {
    this.loading = showLoading;
    this.investments = [];

    combineLatest([
      this.scopeService.investmentStatuses$.pipe(first()),
      this.scopeService.investmentBudgetOrigins$.pipe(first()),
      this.roadmapService.roadmapIndicators().pipe(first())
    ]).subscribe({
      next: ([statuses, budgetOrigins, roadmapIndicators]) => {
        const indicatorsMap = new Map(roadmapIndicators.map(ind => [ind.id, ind]));
        this.investments = this.investmentData
          .filter(inv => inv !== null && inv !== undefined)
          .map(inv => {
            const statusId = typeof inv.status === 'object' && (inv.status as any)?.id
              ? (inv.status as any).id
              : inv.status;
            const status = statuses[statusId];
            const statusName = status?.name || this.translate.instant('Unknown');

            const priceFromSlices = inv.slices?.length
              ? InvestmentsService.getInvestmentTotalPrice(inv as KPI.Investment)
              : 0;
            const budget = priceFromSlices || (Number(inv.current_price) || 0);
            const budgetFormatted = this.i18n.formatCurrency(budget);

            const year = inv.final_schedule_to || inv.final_schedule || new Date().getFullYear();

            let estimateText = '';
            if (inv.budget_origin) {
              if (typeof inv.budget_origin === 'object' && 'name' in inv.budget_origin && inv.budget_origin.name) {
                estimateText = inv.budget_origin.name;
              } else {
                let budgetOriginId: number | null = null;
                if (typeof inv.budget_origin === 'number') {
                  budgetOriginId = inv.budget_origin;
                } else if (typeof inv.budget_origin === 'object' && inv.budget_origin !== null) {
                  budgetOriginId = (inv.budget_origin as any)?.id || null;
                } else if (typeof inv.budget_origin === 'string') {
                  budgetOriginId = Number(inv.budget_origin) || null;
                }

                if (budgetOriginId !== null && !isNaN(budgetOriginId)) {
                  const budgetOrigin = budgetOrigins[budgetOriginId];
                  estimateText = budgetOrigin?.name || '';
                }
              }
            }

            const indicatorIds = inv.impact_roadmap_indicators || [];
            const impactIndicators = Array.isArray(indicatorIds) && indicatorIds.length > 0
              ? indicatorIds
                .map((indicatorId: number) => {
                  const indicator = indicatorsMap.get(indicatorId);
                  return indicator?.name || '';
                })
                .filter((name: string) => name && name.trim())
                .join(', ')
              : '';

            return {
              id: inv.id,
              label: inv.label || this.translate.instant('Unnamed investment'),
              status: statusName,
              budget: budget,
              budgetFormatted: budgetFormatted,
              year: year,
              estimateText: estimateText,
              impactIndicators: impactIndicators,
              investment: inv as KPI.MappedInvestment
            };
          });
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadInvestments(): void {
    this.loading = true;
    this.investments = [];

    combineLatest([
      this.scopeService.investmentStatuses$.pipe(first()),
      this.scopeService.investmentBudgetOrigins$.pipe(first()),
      this.roadmapService.roadmapIndicators().pipe(first()),
      ...this.investmentIds.map(id => this.investmentsService.getInvestment(id))
    ]).pipe(
      map(([statuses, budgetOrigins, roadmapIndicators, ...investments]) => {
        const indicatorsMap = new Map(roadmapIndicators.map(ind => [ind.id, ind]));
        return investments
          .filter(inv => inv !== null && inv !== undefined)
          .map(inv => {
            const statusId = typeof inv.status === 'object' && (inv.status as any)?.id
              ? (inv.status as any).id
              : inv.status;
            const status = statuses[statusId];
            const statusName = status?.name || this.translate.instant('Unknown');

            const budget = InvestmentsService.getInvestmentTotalPrice(inv as KPI.Investment);
            const budgetFormatted = this.i18n.formatCurrency(budget);

            const year = inv.final_schedule_to || inv.final_schedule || new Date().getFullYear();

            let estimateText = '';
            if (inv.budget_origin) {
              if (typeof inv.budget_origin === 'object' && 'name' in inv.budget_origin && inv.budget_origin.name) {
                estimateText = inv.budget_origin.name;
              } else {
                let budgetOriginId: number | null = null;
                if (typeof inv.budget_origin === 'number') {
                  budgetOriginId = inv.budget_origin;
                } else if (typeof inv.budget_origin === 'object' && inv.budget_origin !== null) {
                  budgetOriginId = (inv.budget_origin as any)?.id || null;
                } else if (typeof inv.budget_origin === 'string') {
                  budgetOriginId = Number(inv.budget_origin) || null;
                }

                if (budgetOriginId !== null && !isNaN(budgetOriginId)) {
                  const budgetOrigin = budgetOrigins[budgetOriginId];
                  estimateText = budgetOrigin?.name || '';
                }
              }
            }

            const indicatorIds = inv.impact_roadmap_indicators || [];
            const impactIndicators = Array.isArray(indicatorIds) && indicatorIds.length > 0
              ? indicatorIds
                .map((indicatorId: number) => {
                  const indicator = indicatorsMap.get(indicatorId);
                  return indicator?.name || '';
                })
                .filter((name: string) => name && name.trim())
                .join(', ')
              : '';

            return {
              id: inv.id,
              label: inv.label || this.translate.instant('Unnamed investment'),
              status: statusName,
              budget: budget,
              budgetFormatted: budgetFormatted,
              year: year,
              estimateText: estimateText,
              impactIndicators: impactIndicators,
              investment: inv as KPI.MappedInvestment
            };
          });
      })
    ).subscribe({
      next: (items) => {
        this.investments = items;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadOpex(): void {
    if (this.assetId) {
      this.loadingOpex = true;
      this.opexItems = [];
      this.api.assets_opex({ asset: this.assetId }).subscribe({
        next: (opex) => {
          this.opexItems = opex;
          this.loadingOpex = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingOpex = false;
          this.cdr.detectChanges();
        },
      });
    } else {
      this.opexItems = [];
      this.loadingOpex = false;
    }
  }

  public formatCurrency(value: number): string {
    return this.i18n.formatCurrency(value);
  }

  public formatOpexAmount(value: number): string {
    return this.i18n.formatPriceWithThousands(value);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public close(): void {
    this.activeTab = 'capex';
    this.drawerClosed.emit();
  }

  public openInvestment(investmentId: number): void {
    this.investmentSelected.emit(investmentId);
  }

  public toggleAttachMenu(): void {
    this.showAttachMenu = !this.showAttachMenu;
  }

  public closeAttachMenu(): void {
    this.showAttachMenu = false;
  }

  public openAttachExistingInvestment(): void {
    this.closeAttachMenu();
    this.openAttachInvestmentDialog();
  }

  public openAttachNewInvestment(): void {
    this.closeAttachMenu();

    const perimeter = this.getPerimeterForAttachment();
    if (!perimeter) {
      return;
    }

    this.ivWizardService.prefill({
      ORIGIN_ASSET: this.asset,
      SITE_SELECTION: perimeter,
    }, 'INVESTMENT_TYPE_SELECTION');

    const dialogRef = this.sideFormLevelService.open(
      InvestmentWizardRootComponent,
      {
        autoFocus: true,
        closeOnBackdropClick: false,
        closeOnEsc: false,
      },
    );

    dialogRef.onClose.pipe(first()).subscribe((investCreated) => {
      if (investCreated) {
        if (this.investmentIds && this.investmentIds.length > 0) {
          this.handleVisibilityChange();
        } else if (this.investmentData && this.investmentData.length > 0) {
          this.loadInvestmentsFromData();
        }
      }
    });
  }

  public canAttachInvestment(): boolean {
    return !!this.asset && !this.loading && !this.hasReplacementInvestment();
  }

  private hasReplacementInvestment(): boolean {
    return this.investments.some((item) => {
      const investment = item.investment as any;
      return !!(
        investment?.investment_type_object?.replacement ||
        investment?.investment_type?.replacement
      );
    });
  }

  private getPerimeterForAttachment(): KPI.Perimeter | undefined {
    if (!this.asset?.perimeters?.length) {
      return undefined;
    }

    const sitePerimeter = this.asset.perimeters.find(p => !p.is_monosite);
    return sitePerimeter;
  }

  private openAttachInvestmentDialog(): void {
    const perimeter = this.getPerimeterForAttachment();
    if (!perimeter) {
      return;
    }

    const dialogRef = this.sideFormLevelService.open(
      LinkableInvestmentsListComponent,
      {
        autoFocus: false,
        context: {
          linkedToId: null,
          perimeter: perimeter,
          startYear: 1950,
          endYear: 2100,
          inputLinkedElementIds: this.investments.map(item => item.id),
          multipleChoice: false,
          asset: this.asset
        }
      }
    );

    dialogRef.onClose.pipe(first()).subscribe((investmentIds: number[] | undefined) => {
      if (investmentIds && investmentIds.length > 0) {
        this.investmentsAttached.emit(investmentIds);
      }
    });
  }

  unlinkGlobalInvestment(investment: KPI.MinimalInvestment) {
    this.assetId && this.api.investments_detach_asset(investment.id, this.assetId).pipe(first()).subscribe();
  }

  public unlinkInvestment(investmentItem: InvestmentListItem) {
    if (investmentItem.investment?.investment_type_object?.replacement) {
        this.dialogService.open(BasicDialogComponent, {
          context: {
            title: this.translate.instant('Detach investment'),
            content: this.translate.instant("You can't detach the equipment for an investment of type replacement") + '.',
            contentBis: this.translate.instant("If the linked equipment isn't the correct one, please delete the investment then recreate it choosing the correct linked equipment") + "."
          }
        });
        return;
      }
      this.dialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('Detach investment'),
          content: this.translate.instant('Do you want to continue?'),
          canCancel: true,
        }
      }).onClose.subscribe(res => {
        if (res) {
          this.investmentsService.detachInvestmentAndAsset(investmentItem.id, this.assetId).pipe(first())
          .subscribe(() => {
            this.investmentsAttached.emit(null);
          })
        }
      })
  }
}
